import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import {
  clearWebPushMessage, disableWebPush, dismissWebPushInvitation, enableWebPush,
  getServerWebPushState, getWebPushState, listenForWebPushOpens, openForegroundPush,
  refreshWebPush, setWebPushAccount, showForegroundPush, subscribeWebPush,
} from "../../../services/webPush";
import styles from "./PushNotifications.module.scss";
import { readWebPushDiagnostics, recordWebPushDiagnostic } from "../../../services/webPushDiagnostics";

const usePushState = () => useSyncExternalStore(subscribeWebPush, getWebPushState, getServerWebPushState);

export const PushNotifications = () => {
  const location = useLocation();
  const state = usePushState();
  const cardRef = useRef<HTMLDivElement>(null);
  const [diagnostics, setDiagnostics] = useState<ReturnType<typeof readWebPushDiagnostics> | null>(null);

  useEffect(() => {
    const syncAccount = () => {
      try {
        const auth = JSON.parse(localStorage.getItem("mamiVendorXAuth") || "{}");
        const id = String(auth?.userProfile?.id || "");
        const accessToken = auth?.access_token || "";
        setWebPushAccount(id && accessToken ? { id, accessToken } : null);
      } catch { setWebPushAccount(null); }
    };
    syncAccount();
    window.addEventListener("storage", syncAccount);
    return () => window.removeEventListener("storage", syncAccount);
  }, [location.key]);

  useEffect(() => {
    const onVisible = () => {
      recordWebPushDiagnostic("page_visibility_changed", {
        visibility: document.visibilityState,
        focused: document.hasFocus(),
      });
      if (document.visibilityState === "visible") refreshWebPush();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  useEffect(() => listenForWebPushOpens(), []);

  useEffect(() => {
    const debugWindow = window as Window & { mamiVendorPushDiagnostics?: typeof readWebPushDiagnostics };
    debugWindow.mamiVendorPushDiagnostics = readWebPushDiagnostics;
    return () => { delete debugWindow.mamiVendorPushDiagnostics; };
  }, []);

  useEffect(() => {
    if (!state.notification) return;
    recordWebPushDiagnostic("foreground_card_rendered", { notificationId: state.notification.notificationId });
    const card = cardRef.current;
    if (!card) return;
    const bounds = card.getBoundingClientRect();
    const style = window.getComputedStyle(card);
    const centerX = Math.min(window.innerWidth - 1, Math.max(0, bounds.left + bounds.width / 2));
    const centerY = Math.min(window.innerHeight - 1, Math.max(0, bounds.top + bounds.height / 2));
    const topElement = document.elementFromPoint(centerX, centerY);
    const visible = bounds.width > 0 && bounds.height > 0 && bounds.bottom > 0 &&
      bounds.top < window.innerHeight && bounds.right > 0 && bounds.left < window.innerWidth &&
      style.visibility !== "hidden" && style.display !== "none" && Number(style.opacity) > 0 &&
      !!topElement && card.contains(topElement);
    recordWebPushDiagnostic("foreground_card_visibility", {
      notificationId: state.notification.notificationId,
      outcome: visible ? "visible" : "hidden",
      detail: `z-index:${style.zIndex}`,
    });
  }, [state.notification]);

  const invite = state.accountId && !state.dismissed && (state.status === "off" || state.status === "error");
  return <>
    {process.env.NODE_ENV === "development" && <div className={styles.devTools}>
      <button type="button" onClick={() => showForegroundPush({ data: {
        notificationId: crypto.randomUUID(),
        title: "MamiMarkets vendor UI test",
        body: "This card was generated locally without Firebase.",
        deepLink: "/app/notification-settings",
      } }, "synthetic")}>Show test notification</button>
      <button type="button" onClick={() => setDiagnostics(readWebPushDiagnostics().slice(-35))}>Show push diagnostics</button>
      {diagnostics && <div role="region" aria-label="Push diagnostics">
        <button type="button" onClick={() => setDiagnostics(null)}>Close diagnostics</button>
        <pre>{JSON.stringify(diagnostics, null, 2)}</pre>
      </div>}
    </div>}
    {invite && <aside className={styles.invite} aria-label="Enable notifications">
      <div><strong>Get MamiMarkets notifications</strong><p>Allow browser alerts for updates while you use this device.</p></div>
      <button type="button" onClick={() => void enableWebPush()}>Enable notifications</button>
      <button type="button" className={styles.dismiss} onClick={dismissWebPushInvitation}>Not now</button>
    </aside>}
    {state.notification && <div ref={cardRef} className={styles.toast} role="status" data-mami-push-card>
      <button type="button" className={styles.toastContent} onClick={() => {
        const notification = state.notification;
        if (!notification) return;
        openForegroundPush(notification);
      }} aria-label={`Open notification: ${state.notification.title}`}>
        <strong>{state.notification.title}</strong><p>{state.notification.body}</p>
      </button>
      <button type="button" aria-label="Dismiss notification" onClick={() => {
        recordWebPushDiagnostic("foreground_card_dismissed", { notificationId: state.notification?.notificationId });
        clearWebPushMessage();
      }}>×</button>
    </div>}
  </>;
};

export const PushSettingsControl = () => {
  const state = usePushState();
  const enabled = state.status === "enabled";
  const disabled = state.status === "working" || state.status === "unavailable" || !state.accountId;
  return <section className={styles.settings} aria-label="Browser notifications">
    <h2>Browser notifications</h2>
    <p>Receive MamiMarkets updates in this browser when you are signed in.</p>
    <p className={styles.status}>{state.status === "unavailable" ? "Not available in this browser or environment." :
      state.status === "denied" ? "Blocked by browser settings. Allow notifications for this site, then try again." :
      enabled ? "Enabled on this browser." : state.status === "working" ? "Updating…" : "Off on this browser."}</p>
    {state.message && <p className={styles.error} role="alert">{state.message}</p>}
    <button type="button" disabled={disabled || state.status === "denied"} onClick={() => void (enabled ? disableWebPush() : enableWebPush())}>
      {enabled ? "Disable notifications" : "Enable notifications"}
    </button>
  </section>;
};
